// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// search.ts — the opponent. A real engine, because a weak bot makes a deep game feel pointless.
//
// THE HORIZON IS THE ROUND, AND THAT IS WHAT MAKES THIS AFFORDABLE. Once the racks are dealt nobody
// consults the bag until the round ends, so a round is a perfect-information game with no chance
// nodes in it at all. The search runs to the round boundary — where it resolves the round on a copy
// and scores the result exactly — or to its depth limit, and the bag never enters the tree. It
// cannot: `Table` has no bag in it (see rules.ts). Clairvoyance here is a type error rather than a
// matter of discipline.
//
// Above two players the tree is PARANOID: one maximiser and everybody else minimising. Max^n is
// arguably a better model of a four-handed table, but it does not admit alpha-beta, and the depth
// alpha-beta buys is worth more than the modelling nicety — and, more importantly, paranoid makes
// the negative-gap control a single clean variable (W_RIVAL) instead of a change of algorithm.

import {
  CELLS,
  COURSES,
  KINDS,
  SIDE,
  ROW_BONUS,
  COL_BONUS,
  KIND_BONUS,
  courseCap,
  spoilPenalty,
} from './kinds';
import {
  NO_COL,
  SILL,
  SPOIL,
  Table,
  apply,
  canLoad,
  isLegal,
  legalTakes,
  projectedLayScore,
  resolveRound,
  type Move,
} from './rules';

// ---------------------------------------------------------------- evaluation weights
//
// Every one is an exported named constant so the sim can sweep it and the control arms can null it.

/** A partial course is worth what it would lay, discounted by how far off it is. Squared, so a 4/5
 *  course is defended and a 1/5 is not. */
export const W_COURSE = 1.0;
/** A course that cannot finish from what is on the table is dead weight and a spoil risk. */
export const W_ORPHAN = -1.6;
/** Progress toward a row / column / colour bonus. Convex, so the bot finishes what it starts. */
export const W_BONUS = 1.0;
/** The Trowel: worth having while there is another round to lead, a liability once there is not. */
export const W_TROWEL = 1.4;
/** THE SIGNATURE TERM. What your take leaves in the Sill is your rivals' supply. */
export const W_SILL = 0.55;
/** Being the last to take in a round — you get the pick with nothing coming back at you. */
export const W_TEMPO = 0.6;
/**
 * How much this bot cares what everybody else is building. The negative-gap control arm sets this to
 * zero and changes NOTHING else: same transition function, same depth, same node cap, same terms.
 * A control that alters the model instead of the values is a control that lost the plot — it would
 * be wrong about its own future supply too, and you could no longer attribute the gap to anything.
 */
export const W_RIVAL = 1.0;

export interface Weights {
  course: number;
  orphan: number;
  bonus: number;
  trowel: number;
  sill: number;
  tempo: number;
  rival: number;
}

export const DEFAULT_WEIGHTS: Weights = {
  course: W_COURSE,
  orphan: W_ORPHAN,
  bonus: W_BONUS,
  trowel: W_TROWEL,
  sill: W_SILL,
  tempo: W_TEMPO,
  rival: W_RIVAL,
};

export interface Skill {
  readonly id: string;
  readonly name: string;
  readonly depth: number;
  /** Hard node ceiling. Node caps are reproducible across machines; wall clocks are not. */
  readonly nodes: number;
  /** Wall-clock ceiling in ms. 0 disables it — which is what the sim and every test use. */
  readonly timeMs: number;
  /** Probability of throwing the search away and playing a random legal move. */
  readonly slop: number;
  readonly useTT: boolean;
  readonly weights: Weights;
}

const w = (over: Partial<Weights>): Weights => ({ ...DEFAULT_WEIGHTS, ...over });

export const SKILLS: Record<string, Skill> = {
  // Depth 1, and it can still see a course fill and a Spoil cost — an "easy" bot that scatters
  // single tiles into random courses and finishes near zero is not easy, it is broken.
  apprentice: {
    id: 'apprentice',
    name: 'Apprentice',
    depth: 1,
    nodes: 4_000,
    timeMs: 0,
    slop: 0.25,
    useTT: false,
    weights: w({ orphan: 0, sill: 0, tempo: 0, trowel: 0, rival: 0 }),
  },
  journeyman: {
    id: 'journeyman',
    name: 'Journeyman',
    depth: 3,
    nodes: 60_000,
    timeMs: 400,
    slop: 0.04,
    useTT: false,
    weights: w({ sill: 0 }),
  },
  master: {
    id: 'master',
    name: 'Master',
    depth: 12,
    nodes: 260_000,
    timeMs: 700,
    slop: 0,
    useTT: true,
    weights: DEFAULT_WEIGHTS,
  },
};

export const SKILL_IDS: readonly string[] = ['apprentice', 'journeyman', 'master'];
export const skillOf = (id: unknown): Skill =>
  typeof id === 'string' && Object.hasOwn(SKILLS, id) ? SKILLS[id] : SKILLS.master;

// ---------------------------------------------------------------- evaluation

const POP5 = new Uint8Array(32);
for (let i = 0; i < 32; i++) POP5[i] = (i & 1) + ((i >> 1) & 1) + ((i >> 2) & 1) + ((i >> 3) & 1) + ((i >> 4) & 1);

/** Tiles of each kind still on the table — the only supply that matters inside the horizon. */
function tableKinds(t: Table, out: Int16Array): void {
  out.fill(0);
  for (let r = 0; r < t.rackCount; r++) {
    for (let k = 0; k < KINDS; k++) out[k] += t.racks[r * KINDS + k];
  }
  for (let k = 0; k < KINDS; k++) out[k] += t.sill[k];
}

const scratchTable = new Int16Array(KINDS);
const scratchSill = new Float64Array(KINDS);
const scratchKindCount = new Int8Array(KINDS);

/**
 * What one seat's position is worth, in points. Realised score, plus what the courses will lay, plus
 * convex progress toward the end bonuses, minus the Spoil.
 */
function seatValue(t: Table, s: number, supply: Int16Array, wt: Weights): number {
  let v = t.score[s];

  for (let c = 0; c < COURSES; c++) {
    const i = s * COURSES + c;
    const k = t.courseKind[i];
    if (k < 0) continue;
    const cap = courseCap(c);
    const n = t.courseCount[i];
    const frac = n / cap;
    const lay = projectedLayScore(t, s, c, k);
    v += wt.course * frac * frac * lay;
    const need = cap - n;
    if (need > 0 && supply[k] < need) v += wt.orphan * frac * need;
  }

  for (let i = 0; i < SIDE; i++) {
    const rf = POP5[t.rowBits[s * SIDE + i]] / SIDE;
    const cf = POP5[t.colBits[s * SIDE + i]] / SIDE;
    v += wt.bonus * rf * rf * ROW_BONUS;
    v += wt.bonus * cf * cf * COL_BONUS;
  }
  scratchKindCount.fill(0);
  for (let i = 0; i < CELLS; i++) {
    const k = t.mosaic[s * CELLS + i];
    if (k >= 0) scratchKindCount[k]++;
  }
  for (let k = 0; k < KINDS; k++) {
    const f = scratchKindCount[k] / SIDE;
    v += wt.bonus * f * f * KIND_BONUS;
  }

  v -= spoilPenalty(t.spoilTiles[s] + t.trowel[s]);
  return v;
}

/**
 * THE TUMBLE, priced in O(5). For each kind, what a rival could do with one more of it — the best
 * lay any of their courses could reach. What sits in the Sill after your take is exactly what you
 * handed them, and this is the dot product that says how much that was worth.
 *
 * The expensive way to write this term is "the increase in every opponent's best reply", which is a
 * nested search inside every leaf: three opponents x ~13 takes x 6 destinations is 230 lay
 * evaluations per node, and it would cost the search two full plies. This says the same thing.
 */
function sillCost(t: Table, root: number, wt: Weights): number {
  if (wt.sill === 0) return 0;
  scratchSill.fill(0);
  for (let s = 0; s < t.players; s++) {
    if (s === root) continue;
    for (let k = 0; k < KINDS; k++) {
      if (t.sill[k] === 0) continue; // nothing of this kind was handed over, so nothing to price
      let best = 0;
      for (let c = 0; c < COURSES; c++) {
        if (!canLoad(t, s, c, k)) continue;
        const i = s * COURSES + c;
        const need = courseCap(c) - t.courseCount[i];
        const lay = projectedLayScore(t, s, c, k);
        // A take that exactly fills is worth its lay; a take that half-fills is worth part of it.
        const fit = need <= 0 ? 0 : Math.min(1, t.sill[k] / need);
        const val = lay * fit;
        if (val > best) best = val;
      }
      scratchSill[k] += best;
    }
  }
  let cost = 0;
  for (let k = 0; k < KINDS; k++) if (t.sill[k] > 0) cost += scratchSill[k];
  return wt.sill * cost;
}

/** The position, from `root`'s point of view. Paranoid: root's value minus the best rival's. */
export function evaluate(t: Table, root: number, wt: Weights): number {
  tableKinds(t, scratchTable);
  const mine = seatValue(t, root, scratchTable, wt);
  let best = 0;
  if (wt.rival !== 0) {
    let top = -Infinity;
    for (let s = 0; s < t.players; s++) {
      if (s === root) continue;
      const v = seatValue(t, s, scratchTable, wt);
      if (v > top) top = v;
    }
    best = wt.rival * (top === -Infinity ? 0 : top);
  }

  let v = mine - best;
  if (wt.rival !== 0) v -= sillCost(t, root, wt);
  if (t.trowel[root]) {
    // Worth having while another round is coming; a plain minus once somebody is about to end it.
    let nearEnd = false;
    for (let s = 0; s < t.players && !nearEnd; s++) {
      for (let r = 0; r < SIDE; r++) if (POP5[t.rowBits[s * SIDE + r]] >= 4) nearEnd = true;
    }
    v += nearEnd ? -wt.trowel : wt.trowel;
  }
  if (wt.tempo !== 0 && t.tableTiles() === 0 && t.seat === root) v += wt.tempo;
  return v;
}

/** The exact value of the position at the round boundary: resolve the round on a copy and read it. */
function horizonValue(t: Table, root: number, wt: Weights, scratch: Table): number {
  t.copyInto(scratch);
  resolveRound(scratch);
  return evaluate(scratch, root, wt);
}

// ---------------------------------------------------------------- move generation

export interface Cand {
  readonly move: Move;
  /** Cheap ordering score. Not the search value. */
  readonly hint: number;
}

/** How close a runner-up destination has to be before it is worth a branch. */
export const SECOND_MARGIN = 3.5;
/** A take worth less than this to you is a take you might only want in order to deny it. */
export const DENIAL_THRESHOLD = 2.5;
/** How many non-primary candidates (runner-up destinations, denial dumps) a node may carry. */
export const EXTRA_BUDGET = 12;

/**
 * Candidate moves: every (source, kind) take, each with the destination a one-ply exact lay eval
 * likes best — PLUS a dump straight to the Spoil whenever the best destination overflows.
 *
 * That second move is not an optimisation, it is the game. A denial take is precisely a take whose
 * best destination is the Spoil: you eat −5 to stop somebody. A generator that only ever offers the
 * greedy destination cannot see the play this game is built on, at any node, at any depth.
 */
export function candidates(t: Table, wt: Weights): Cand[] {
  const out: Cand[] = [];
  const extra: Cand[] = [];
  const seat = t.seat;
  for (const take of legalTakes(t)) {
    let bestDest = SPOIL;
    let bestCol = NO_COL;
    let bestVal = -Infinity;
    let bestOver = take.count;
    let secondDest = SPOIL;
    let secondCol = NO_COL;
    let secondVal = -Infinity;

    const offer = (c: number, col: number, val: number, over: number): void => {
      if (val > bestVal) {
        secondDest = bestDest;
        secondCol = bestCol;
        secondVal = bestVal;
        bestVal = val;
        bestDest = c;
        bestCol = col;
        bestOver = over;
      } else if (val > secondVal) {
        secondVal = val;
        secondDest = c;
        secondCol = col;
      }
    };

    for (let c = 0; c < COURSES; c++) {
      if (!canLoad(t, seat, c, take.kind)) continue;
      const i = seat * COURSES + c;
      const room = courseCap(c) - t.courseCount[i];
      const placed = Math.min(take.count, room);
      const over = take.count - placed;
      const filled = t.courseCount[i] + placed;
      const frac = filled / courseCap(c);

      if (t.mode.printed || t.courseCol[i] !== NO_COL) {
        const lay = projectedLayScore(t, seat, c, take.kind);
        const val = frac * frac * lay - spoilPenalty(t.spoilTiles[seat] + t.trowel[seat] + over)
          + spoilPenalty(t.spoilTiles[seat] + t.trowel[seat]);
        offer(c, NO_COL, val, over);
      } else {
        // Freehand's column is chosen inside move generation the same way, exactly — there are at
        // most five and each one's lay score is exact.
        for (const col of t.legalCols(seat, c, take.kind)) {
          t.courseCol[i] = col;
          const lay = projectedLayScore(t, seat, c, take.kind);
          t.courseCol[i] = NO_COL;
          const val = frac * frac * lay - spoilPenalty(t.spoilTiles[seat] + t.trowel[seat] + over)
            + spoilPenalty(t.spoilTiles[seat] + t.trowel[seat]);
          offer(c, col, val, over);
        }
      }
    }

    const spoilCost =
      spoilPenalty(t.spoilTiles[seat] + t.trowel[seat] + take.count) -
      spoilPenalty(t.spoilTiles[seat] + t.trowel[seat]);

    if (bestDest !== SPOIL) {
      out.push({ move: { src: take.src, kind: take.kind, dest: bestDest, col: bestCol }, hint: bestVal + 8 });
      // The runner-up destination, because a one-ply lay eval is a hint and not an oracle: the
      // cheapest place to put a tile now is often the course you were saving. Only when it is a real
      // rival, though — a clearly worse course is branching factor spent on nothing.
      if (secondDest !== SPOIL && secondVal > bestVal - SECOND_MARGIN) {
        extra.push({
          move: { src: take.src, kind: take.kind, dest: secondDest, col: secondCol },
          hint: secondVal + 4,
        });
      }
      // The pure denial play: take it and eat the Spoil, purely to stop somebody. This is the move
      // the game is built on, and a generator that only offers the greedy destination cannot see it
      // at any node, at any depth. It is offered whenever the take overflows anyway, or whenever the
      // take does little for you — which is exactly the shape of a take you only want denied. It
      // sorts last, so alpha-beta pays almost nothing for carrying it.
      if (bestOver > 0 || bestVal < DENIAL_THRESHOLD) {
        extra.push({
          move: { src: take.src, kind: take.kind, dest: SPOIL, col: NO_COL },
          hint: -spoilCost - (bestOver > 0 ? 0 : 3),
        });
      }
    } else {
      out.push({ move: { src: take.src, kind: take.kind, dest: SPOIL, col: NO_COL }, hint: -spoilCost });
    }
  }
  // A take from the Sill that costs the Trowel is worth trying late, not first.
  for (const c of out) {
    if (c.move.src === SILL && t.trowelInSill) (c as { hint: number }).hint -= 1.5;
  }
  // EVERY take keeps its best destination — dropping a whole take is how a search stops seeing a
  // move. The alternatives (runner-up, denial) are a budget: the best EXTRA_BUDGET of them by hint.
  // At a four-handed round start that is the difference between b=81 and b=39, which is a whole ply.
  if (extra.length > 0) {
    extra.sort((a, b) => b.hint - a.hint);
    for (let i = 0; i < extra.length && i < EXTRA_BUDGET; i++) out.push(extra[i]);
  }
  out.sort((a, b) => b.hint - a.hint);
  void wt;
  return out;
}

// ---------------------------------------------------------------- transposition table

const TT_BITS = 18;
const TT_SIZE = 1 << TT_BITS;

class TT {
  private readonly key = new Int32Array(TT_SIZE);
  private readonly val = new Float64Array(TT_SIZE);
  private readonly depth = new Int8Array(TT_SIZE);
  private readonly flag = new Int8Array(TT_SIZE);
  private readonly stamp = new Uint8Array(TT_SIZE);
  private gen = 1;

  bump(): void {
    this.gen = (this.gen + 1) & 0xff || 1;
  }

  probe(k: number, d: number): { val: number; flag: number } | null {
    const i = (k >>> (32 - TT_BITS)) & (TT_SIZE - 1);
    if (this.key[i] !== k || this.depth[i] < d) return null;
    return { val: this.val[i], flag: this.flag[i] };
  }

  store(k: number, d: number, v: number, flag: number): void {
    const i = (k >>> (32 - TT_BITS)) & (TT_SIZE - 1);
    if (this.stamp[i] === this.gen && this.depth[i] > d) return;
    this.key[i] = k;
    this.depth[i] = d;
    this.val[i] = v;
    this.flag[i] = flag;
    this.stamp[i] = this.gen;
  }
}

/** FNV-1a over the parts of the table a search node can differ in. */
function hashTable(t: Table): number {
  let h = 0x811c9dc5;
  const mix = (x: number): void => {
    h ^= x & 0xff;
    h = Math.imul(h, 0x01000193);
  };
  for (let i = 0; i < t.racks.length; i++) mix(t.racks[i]);
  for (let i = 0; i < KINDS; i++) mix(t.sill[i]);
  for (let i = 0; i < t.courseKind.length; i++) {
    mix(t.courseKind[i] + 1);
    mix(t.courseCount[i]);
    mix(t.courseCol[i] + 1);
  }
  for (let i = 0; i < t.mosaic.length; i++) mix(t.mosaic[i] + 1);
  for (let s = 0; s < t.players; s++) {
    mix(t.spoilTiles[s]);
    mix(t.trowel[s]);
    mix(t.score[s]);
    mix(t.score[s] >> 8);
    mix(t.fired[s]);
  }
  mix(t.seat);
  mix(t.trowelInSill ? 1 : 0);
  return h | 0;
}

// ---------------------------------------------------------------- the search

export interface SearchResult {
  readonly move: Move | null;
  readonly value: number;
  readonly depth: number;
  readonly nodes: number;
  readonly ms: number;
}

const MAX_DEPTH = 16;

export class Searcher {
  private readonly stack: Table[] = [];
  private readonly tt = new TT();
  private nodes = 0;
  private deadline = 0;
  private cap = 0;
  private aborted = false;
  private root = 0;
  private wt: Weights = DEFAULT_WEIGHTS;
  private scratch: Table | null = null;

  private frame(depth: number, t: Table): Table {
    let f = this.stack[depth];
    if (!f || f.players !== t.players || f.rackCount !== t.rackCount || f.mode !== t.mode) {
      f = t.clone();
      this.stack[depth] = f;
    }
    return f;
  }

  /** Nodes visited by the last `choose`. The sim reports this rather than a wall clock. */
  get lastNodes(): number {
    return this.nodes;
  }

  choose(t: Table, skill: Skill, rand?: () => number): SearchResult {
    const started = Date.now();
    this.nodes = 0;
    this.aborted = false;
    this.root = t.seat;
    this.wt = skill.weights;
    this.cap = skill.nodes;
    this.deadline = skill.timeMs > 0 ? started + skill.timeMs : Number.POSITIVE_INFINITY;
    this.tt.bump();
    if (!this.scratch || this.scratch.players !== t.players || this.scratch.mode !== t.mode) {
      this.scratch = t.clone();
    }

    const cands = candidates(t, this.wt);
    if (cands.length === 0) return { move: null, value: 0, depth: 0, nodes: 0, ms: 0 };

    if (skill.slop > 0 && rand && rand() < skill.slop) {
      const m = cands[Math.floor(rand() * cands.length)].move;
      return { move: m, value: 0, depth: 0, nodes: 0, ms: Date.now() - started };
    }

    let best = cands[0].move;
    let bestVal = -Infinity;
    let reached = 0;

    for (let d = 1; d <= Math.min(skill.depth, MAX_DEPTH); d++) {
      let alpha = -Infinity;
      let localBest = best;
      let localVal = -Infinity;
      let complete = true;
      // Search the previous iteration's best first.
      const order = cands.slice();
      const bi = order.findIndex((c) => sameMove(c.move, best));
      if (bi > 0) order.unshift(order.splice(bi, 1)[0]);

      for (const c of order) {
        const child = this.frame(d, t);
        t.copyInto(child);
        apply(child, c.move);
        const v = this.node(child, d - 1, alpha, Infinity, skill);
        if (this.aborted) {
          complete = false;
          break;
        }
        if (v > localVal) {
          localVal = v;
          localBest = c.move;
        }
        if (v > alpha) alpha = v;
      }
      if (complete) {
        best = localBest;
        bestVal = localVal;
        reached = d;
      }
      if (this.aborted) break;
    }

    return { move: best, value: bestVal, depth: reached, nodes: this.nodes, ms: Date.now() - started };
  }

  private node(t: Table, depth: number, alpha: number, beta: number, skill: Skill): number {
    this.nodes++;
    if ((this.nodes & 0x1ff) === 0) {
      if (this.nodes > this.cap || Date.now() > this.deadline) this.aborted = true;
    }
    if (this.aborted) return evaluate(t, this.root, this.wt);
    if (t.over) return evaluate(t, this.root, this.wt);
    if (t.roundOver()) return horizonValue(t, this.root, this.wt, this.scratch!);
    if (depth <= 0) return evaluate(t, this.root, this.wt);

    let key = 0;
    if (skill.useTT) {
      key = hashTable(t);
      const hit = this.tt.probe(key, depth);
      if (hit && hit.flag === 0) return hit.val;
    }

    const maximising = t.seat === this.root;
    const cands = candidates(t, this.wt);
    if (cands.length === 0) return evaluate(t, this.root, this.wt);

    let value = maximising ? -Infinity : Infinity;
    let a = alpha;
    let b = beta;
    for (const c of cands) {
      const child = this.frame(depth, t);
      t.copyInto(child);
      apply(child, c.move);
      const v = this.node(child, depth - 1, a, b, skill);
      if (this.aborted) return value === Infinity || value === -Infinity ? v : value;
      if (maximising) {
        if (v > value) value = v;
        if (value > a) a = value;
      } else {
        if (v < value) value = v;
        if (value < b) b = value;
      }
      if (b <= a) break;
    }
    if (skill.useTT) this.tt.store(key, depth, value, 0);
    return value;
  }
}

const sameMove = (a: Move, b: Move): boolean =>
  a.src === b.src && a.kind === b.kind && a.dest === b.dest && a.col === b.col;

/** One-shot convenience: pick a move for whoever is to play. */
export function chooseMove(t: Table, skill: Skill, rand?: () => number): Move | null {
  const s = new Searcher();
  const r = s.choose(t, skill, rand);
  return r.move && isLegal(t, r.move) ? r.move : null;
}
